import React from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { FaHome, FaTools, FaEnvelope } from "react-icons/fa";
import { useTranslation } from 'react-i18next';

/**
 * 404.
 *
 * Every link here was styled with two interpolated Tailwind class names —
 * `hover:${primaryTextColor}` and `bg-${accentColor}` — which the build cannot
 * see and therefore never generated. The result: the hover colour and the
 * animated underline simply did not exist, on the one page where a visitor is
 * already lost and the links are the entire point.
 */
const NotFound = () => {
    const { t } = useTranslation();
    const [contentRef, contentInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";

    const links = [
        { to: "/", icon: <FaHome className="mr-2" />, label: t('notFound.backToHome') },
        { to: "/services", icon: <FaTools className="mr-2" />, label: t('notFound.browseServices') },
        { to: "/contact", icon: <FaEnvelope className="mr-2" />, label: t('notFound.contactUs') },
    ];

    return (
        <div className="bg-black min-h-screen flex items-center justify-center font-sans">
            <div
                ref={contentRef}
                className={`container mx-auto px-4 py-16 text-center transition-opacity duration-1000 ${contentInView ? "opacity-100" : "opacity-0"
                    }`}
            >
                <div className="max-w-2xl mx-auto">
                    <div className="relative mb-8">
                        <h1 className="text-9xl font-serif font-medium text-moss-deep/40">404</h1>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h1 className={`text-9xl font-serif font-medium ${primaryTextColor}`}>
                                404
                            </h1>
                        </div>
                    </div>

                    <h2 className={`text-3xl font-serif font-medium mb-4 ${primaryTextColor}`}>
                        {t('notFound.title')}
                    </h2>

                    <p className={`text-xl ${secondaryTextColor} mb-12 leading-relaxed`}>
                        {t('notFound.message')}
                    </p>

                    <div className="flex flex-col md:flex-row gap-8 justify-center">
                        {links.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`relative group inline-flex items-center px-1 pt-1 pb-2 text-lg font-medium ${secondaryTextColor} transition-colors duration-300 hover:text-silver-light`}
                            >
                                {link.icon}
                                {link.label}
                                <span className="absolute bottom-0 left-0 block h-px w-full origin-left scale-x-0 bg-moss transition-transform duration-300 ease-out group-hover:scale-x-100" />
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotFound;
